import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sparkles,
  Archive,
  LineChart,
  BarChart3,
  Waves,
  Brain,
  HelpCircle,
  Compass,
  Download,
  Bell,
  Cloud,
} from "lucide-react";
import { Screen } from "@/components/Screen";
import { PremiumBadge } from "@/components/PremiumBadge";
import { PremiumFeatureCard } from "@/components/PremiumFeatureCard";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * FEATURE_META — the icon per feature key.
 * Keep this in sync with translations.premium.features.<key>.
 * Exported so /settings can reuse the same order + icons.
 */
export const FEATURES = [
  { key: "fullHistory", Icon: Archive },
  { key: "weeklyReports", Icon: LineChart },
  { key: "monthlyReports", Icon: BarChart3 },
  { key: "patternAnalysis", Icon: Waves },
  { key: "deeperReflections", Icon: Brain },
  { key: "customQuestions", Icon: HelpCircle },
  { key: "dayModes", Icon: Compass },
  { key: "exportNotes", Icon: Download },
  { key: "gentleReminders", Icon: Bell },
];

const DAY_MODES = ["founder", "calmDay", "deepWork", "recovery", "decision"];

export default function Premium() {
  const { t } = useI18n();
  const navigate = useNavigate();

  return (
    <Screen withBottomNav={false} testId="premium-screen">
      <Link
        to="/settings"
        data-testid="premium-back-link"
        className="inline-flex items-center gap-1.5 text-sm text-[#CBD5E1] hover:text-white transition-colors duration-200 mb-6 mt-2 rounded-md px-1 py-0.5"
      >
        <ArrowLeft size={16} strokeWidth={1.6} />
        {t("common.backToSettings")}
      </Link>

      {/* Hero */}
      <section className="text-center pt-2">
        <div
          className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center relative"
          style={{
            background:
              "linear-gradient(135deg, rgba(244,201,93,0.22), rgba(167,139,250,0.20))",
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              background:
                "radial-gradient(circle, rgba(244,201,93,0.24) 0%, rgba(244,201,93,0) 70%)",
              filter: "blur(6px)",
            }}
          />
          <Sparkles
            size={28}
            className="text-[#F4C95D] relative"
            strokeWidth={1.6}
          />
        </div>
        <p
          className="mt-5 text-xs uppercase tracking-[0.22em] text-[#E8D8B8]/80"
          style={{ letterSpacing: "0.22em" }}
        >
          {t("app.tagline")}
        </p>
        <h1
          className="mt-2 text-3xl sm:text-4xl font-medium leading-tight"
          style={{ letterSpacing: "-0.025em" }}
          data-testid="premium-title"
        >
          {t("premium.hero.title")}
        </h1>
        <p className="mt-3 text-base sm:text-lg text-[#E8D8B8]">
          {t("premium.hero.subtitle")}
        </p>
        <div className="mt-4 flex justify-center">
          <PremiumBadge tone="gold" testId="premium-hero-badge" />
        </div>
      </section>

      {/* Description */}
      <section className="mt-10 space-y-3" data-testid="premium-description">
        <p className="text-[15px] text-[#CBD5E1] leading-relaxed">
          {t("premium.hero.description.p1")}
        </p>
        <p className="text-[15px] text-[#CBD5E1] leading-relaxed">
          {t("premium.hero.description.p2")}
        </p>
      </section>

      {/* Feature list */}
      <section className="mt-10" data-testid="premium-features">
        <SectionEyebrow textKey="premium.sections.features" />
        <div className="mt-4 space-y-3">
          {FEATURES.map(({ key, Icon }) => (
            <PremiumFeatureCard
              key={key}
              Icon={Icon}
              titleKey={`premium.features.${key}.title`}
              descriptionKey={`premium.features.${key}.description`}
              variant="full"
              testId={`premium-feature-${key}`}
            >
              {key === "dayModes" ? <DayModesChips t={t} /> : null}
            </PremiumFeatureCard>
          ))}
        </div>
      </section>

      {/* Sync — separate, softer section below the 9-card grid */}
      <section
        className="mt-6 rounded-2xl p-4 border border-white/[0.05] bg-white/[0.02]"
        data-testid="premium-feature-sync"
      >
        <div className="flex items-start gap-2.5">
          <Cloud
            size={15}
            className="text-[#A78BFA]/80 mt-0.5 flex-none"
            strokeWidth={1.6}
          />
          <div className="flex-1 min-w-0">
            <h3
              className="text-[13px] font-medium text-[#F8FAFC]/90 leading-snug"
              style={{ letterSpacing: "-0.005em" }}
            >
              {t("premium.sync.title")}
            </h3>
            <p className="mt-1 text-[12px] text-[#94A3B8] leading-relaxed">
              {t("premium.sync.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section
        className="mt-8 px-2"
        data-testid="premium-closing"
      >
        <p className="text-[14px] text-[#CBD5E1] leading-relaxed italic">
          {t("premium.closing")}
        </p>
      </section>

      {/* Footer button — back to settings only, NO purchase CTA */}
      <div className="mt-10">
        <button
          type="button"
          onClick={() => navigate("/settings")}
          data-testid="premium-footer-back"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-medium text-white/80 border border-white/12 hover:bg-white/[0.05] hover:text-white transition-colors duration-200"
        >
          <ArrowLeft size={14} strokeWidth={1.7} />
          {t("common.backToSettings")}
        </button>
      </div>
    </Screen>
  );
}

function DayModesChips({ t }) {
  return (
    <div
      className="mt-3 flex flex-wrap gap-1.5"
      data-testid="premium-day-modes"
    >
      {DAY_MODES.map((k) => (
        <span
          key={k}
          className="text-[11px] px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-[#E8D8B8]"
          style={{ letterSpacing: "-0.005em" }}
        >
          {t(`premium.dayModes.${k}`)}
        </span>
      ))}
    </div>
  );
}

function SectionEyebrow({ textKey }) {
  const { t } = useI18n();
  return (
    <h2
      className="text-[11px] uppercase tracking-[0.18em] text-[#E8D8B8]/80"
      style={{ letterSpacing: "0.18em" }}
    >
      {t(textKey)}
    </h2>
  );
}
